import { useEffect } from "react";

/**
 * VSL variant #1 — full-width video with all the credibility copy
 * (headline, subhead, prior-results disclaimer, stat bar) from the standard
 * Child Custody landing page kept intact ABOVE the video. This way the
 * visitor gets the same context they'd see on the standard page, then the
 * video pitch, then a single CTA that anchors to the consultation form.
 */
export default function HeroVSL({
  videoId = "6a4e8bbf3c6dffade0651e08",
  accountId = "266ac8d1-c8a5-46ce-a380-8520236571df",
  eyebrow = "Family Law Attorney · Orange County, California",
  ctaLabel = "Schedule Your Confidential Consultation",
  ctaHref = "#consultation",
  headline,
  subhead,
  statBar,
  showStatBar = true,
} = {}) {
  useEffect(() => {
    const src = `https://scripts.converteai.net/${accountId}/players/${videoId}/v4/player.js`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    document.head.appendChild(s);
  }, [accountId, videoId]);

  const defaultHeadline = (
    <>
      You&rsquo;re not just fighting a case.{" "}
      <span className="italic text-teal">
        You&rsquo;re fighting for your child.
      </span>
    </>
  );
  const defaultSubhead = (
    <>
      Jos Family Law was founded by{" "}
      <span className="text-ink font-medium">Binoye Jos, Esq.</span> — a father
      who spent <span className="text-ink font-medium">$400,000</span> with{" "}
      <span className="text-ink font-medium">two prominent law firms</span> who
      told him he&rsquo;d never get equal custody. He fired them, represented
      himself, and won{" "}
      <span className="text-ink font-medium">equal custody</span> of his own
      son. He then went to law school and built this firm so you don&rsquo;t
      have to go through what he did.
    </>
  );
  const defaultStats = [
    { num: "2", label: "Law Firms Fired" },
    { num: "$400K+", label: "In Fees" },
    { num: "Equal", label: "Custody Won" },
  ];

  return (
    <section
      data-testid="hero-vsl"
      className="relative bg-paper text-ink pt-10 md:pt-14 pb-14 md:pb-20"
    >
      <div className="max-w-[1080px] mx-auto px-5 md:px-12 lg:px-20">
        {/* Full credibility block (matches the standard landing) */}
        <div className="max-w-[820px] mx-auto text-center">
          <p
            data-testid="hero-vsl-eyebrow"
            className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-gray-600 mb-5"
          >
            {eyebrow}
          </p>

          <h1
            data-testid="hero-vsl-headline"
            className="font-display text-[36px] sm:text-[44px] md:text-[60px] leading-[1.02] tracking-tight text-ink"
          >
            {headline || defaultHeadline}
          </h1>

          <p
            data-testid="hero-vsl-subhead"
            className="mt-8 text-[16px] md:text-[18px] leading-[1.7] text-gray-800 mx-auto max-w-[62ch]"
          >
            {subhead || defaultSubhead}
          </p>

          <p
            data-testid="hero-vsl-prior-results"
            className="mt-4 font-body text-[12px] leading-relaxed italic text-gray-600"
          >
            Prior results do not guarantee a similar outcome.
          </p>
        </div>

        {/* Video */}
        <div className="relative mx-auto max-w-[880px] mt-12 md:mt-14">
          <p className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-gray-600 mb-4 text-center">
            A Message From Binoye Jos, Esq.
          </p>
          <vturb-smartplayer
            id={`vid-${videoId}`}
            style={{ display: "block", margin: "0 auto", width: "100%" }}
            data-testid="vturb-smartplayer"
          >
            <div
              className="vturb-player-placeholder"
              style={{
                position: "relative",
                width: "100%",
                padding: "56.25% 0 0",
                zIndex: 0,
                backgroundColor: "black",
              }}
            />
          </vturb-smartplayer>
        </div>

        {/* CTA button anchored to the consultation form */}
        <div className="text-center mt-10 md:mt-12">
          <a
            href={ctaHref}
            data-testid="hero-vsl-cta"
            className="inline-flex items-center gap-3 bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-10 py-5 min-w-[320px] justify-center"
          >
            <span className="font-mono text-[12px] md:text-[13px] tracking-[0.24em] uppercase font-semibold">
              {ctaLabel}
            </span>
          </a>
          <p className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600">
            Confidential · No obligation
          </p>
        </div>

        {/* Stat bar */}
        {showStatBar && (
          <>
            <div
              data-testid="hero-vsl-stat-bar"
              className="mt-16 md:mt-20 rule-top rule-bottom py-10 grid grid-cols-3 gap-3 sm:gap-4 md:gap-8 max-w-[880px] mx-auto"
            >
              {(statBar || defaultStats).map((s, i) => (
                <div
                  key={s.label}
                  data-testid={`hero-vsl-stat-${i}`}
                  className="flex flex-col items-start"
                >
                  <span className="font-display text-[32px] sm:text-[40px] md:text-[52px] leading-none tracking-tight text-ink">
                    {s.num}
                  </span>
                  <span className="mt-3 font-mono text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.2em] sm:tracking-[0.24em] uppercase text-gray-600">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 max-w-[880px] mx-auto font-body text-[12px] leading-relaxed italic text-gray-600">
              Prior results do not guarantee a similar outcome.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
